"use client";

import { useState } from "react";
import { useAuth, useUser } from "@clerk/nextjs";
import { apiFetchAuthed } from "@/lib/api";
import { eventDetailLog as log } from "@/lib/logger";
import { useToast } from "@/components/ui/Toast";
import type { PublicEvent } from "./EventTypes";

function formatIcsDate(date: Date) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  const h = String(date.getUTCHours()).padStart(2, "0");
  const min = String(date.getUTCMinutes()).padStart(2, "0");
  const s = String(date.getUTCSeconds()).padStart(2, "0");
  return `${y}${m}${d}T${h}${min}${s}Z`;
}

function escapeIcs(value: string) {
  return value.replace(/\\/g, "\\\\").replace(/,/g, "\\,").replace(/;/g, "\\;").replace(/\n/g, "\\n");
}

function buildIcs(event: PublicEvent) {
  const start = new Date(event.date);
  if (Number.isNaN(start.getTime())) {
    return null;
  }
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const uid = `${event.id}@hopp.life`;
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Hopp//Events//EN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${formatIcsDate(new Date())}`,
    `DTSTART:${formatIcsDate(start)}`,
    `DTEND:${formatIcsDate(end)}`,
    `SUMMARY:${escapeIcs(event.title)}`,
    `DESCRIPTION:${escapeIcs(event.description ?? "")}`,
    `LOCATION:${escapeIcs(`${event.venue} ${event.location}`.trim())}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return lines.join("\r\n");
}

export function useEventActions(event: PublicEvent | undefined) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const toaster = useToast();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleShare = async () => {
    if (!event || typeof navigator === "undefined") {
      return;
    }
    try {
      if (navigator.share) {
        log.info("share", "using native share");
        await navigator.share({
          title: event.title ?? "Event",
          url: window.location.href,
        });
        return;
      }
      if (navigator.clipboard) {
        log.info("share", "copying to clipboard");
        await navigator.clipboard.writeText(window.location.href);
        toaster.success("Link copied to clipboard");
      }
    } catch (err) {
      log.warn("share", "failed", { error: err instanceof Error ? err.message : String(err) });
    }
  };

  const handleWhatsAppShare = () => {
    if (!event) return;
    const dateStr = event.date
      ? new Date(event.date).toLocaleDateString("en-IN", { weekday: "short", month: "short", day: "numeric", timeZone: "Asia/Kolkata" })
      : "";
    const parts = [event.title ?? "Event"];
    if (dateStr) parts.push(dateStr);
    if (event.venue) parts.push(event.venue);
    parts.push(window.location.href);
    const text = encodeURIComponent(parts.join(" | "));
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  const handleAddToCalendar = () => {
    if (!event) {
      return;
    }
    log.info("calendar", "downloading ICS", { eventId: event.id });
    const ics = buildIcs(event);
    if (!ics) {
      return;
    }
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${event.title || "event"}.ics`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleToggleSave = async () => {
    if (!event || !isLoaded) {
      return;
    }
    if (!isSignedIn) {
      return { needsAuth: true };
    }
    if (saving) {
      return;
    }
    log.info("toggleSave", "toggling", { eventId: event.id, currentSaved: saved });

    const previousSaved = saved;
    setSaved(!saved); // Optimistic flip
    setSaving(true);
    const stop = log.time("toggleSave");
    try {
      const data = await apiFetchAuthed<{ saved: boolean }>(
        `events/${event.id}/save`,
        { method: "POST" },
        () => getToken(),
      );
      stop();
      log.info("toggleSave", "done", { saved: data.saved });
      setSaved(Boolean(data.saved)); // Reconcile with server
      toaster.success(data.saved ? "Event saved" : "Event unsaved");
    } catch (err) {
      stop();
      setSaved(previousSaved); // Rollback on error
      log.warn("toggleSave", "failed", { error: err instanceof Error ? err.message : String(err) });
      toaster.error("Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  return {
    saved,
    setSaved,
    saving,
    handleShare,
    handleWhatsAppShare,
    handleAddToCalendar,
    handleToggleSave,
  };
}
